import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import ApprovedContainer from '../ApprovedContainer/ApprovedContainer.jsx';
import { message, Modal, Button, Skeleton, Checkbox } from 'antd';
import { withApollo } from 'react-apollo';
import { motion } from 'motion/react';
import { gql, useQuery, useApolloClient } from '@apollo/client';
import { addCommas } from 'src/rewardsAndRecognitionHelper/utils.js';
import {
	fetchModernFormQuestions,
	snappy,
} from 'src/_shared/services/utils.js';
import { useSelector } from 'react-redux';
import uuid from 'uuid/v4';

const QUERY_BADGE_ASSIGNMENTS_ADMIN = gql`
	query QueryBadgeAssignmentsAdmin(
		$companyId: ID!
		$badgeId: ID
		$approvalStatus: ApprovalStatus
		$filterAutomated: Boolean
		$subCompanyIds: [ID]
		$nextToken: String
		$limit: Int
	) {
		queryBadgeAssignmentsAdmin(
			companyId: $companyId
			badgeId: $badgeId
			approvalStatus: $approvalStatus
			filterAutomated: $filterAutomated
			subCompanyIds: $subCompanyIds
			nextToken: $nextToken
			limit: $limit
		) {
			items {
				id
				approvalStatus
				awardedBy
				badgeId
				companyId
				userGroupId
				managerAssigned
				sentWithoutSnappyGrant
				userId
				accountClaimId
				createdAt
				updatedAt
				completedAt
				declineReason
				quietDecline
				badge {
					id
					name
					category
					description
					points
					bonusPoints
					rewardBonus
					label
					badgeTypeId
					iconId
					color
					iconColor
					isAssignmentRestricted
					gradientColor
					shape
					snappyGrantId
					snappyGrant {
						id
						name
						costInCurrency
						externalSnappyId
						snappyCampaignTierId
					}
				}
				content {
					message
					gifURL
					tags
				}
				rewardBonus
				justificationFormAnswers
				accountClaim {
					id
					firstName
					lastName
					emailAddress
					subCompany
					department
					employeeId
					userId
					customValues
				}
				awardedByUser {
					id
					firstName
					lastName
					emailAddress
					employeeId
					accountClaim {
						id
						branchLocation
						customValues
					}
				}
				recipients {
					accountClaimId
					accountClaim {
						id
						firstName
						lastName
						branchLocation
						emailAddress
						subCompany
						department
						employeeId
						uniqueEmployeeId
						userId
						customValues
					}
					userId
					user {
						id
						firstName
						lastName
						emailAddress
						departmentId
						employeeId
						subCompanyId
						userGroupId
					}
				}
				user {
					id
					firstName
					lastName
					emailAddress
					recognitionEvents {
						currentBudget
					}
				}
			}
			nextToken
		}
	}
`;

// Separate query for detailed user data when needed
const QUERY_USER_RECOGNITION_EVENTS = gql`
	query QueryUserRecognitionEvents($userId: ID!) {
		getUser(id: $userId) {
			id
			firstName
			lastName
			emailAddress
			recognitionEvents {
				currentBudget
				amountPending
				additionalBudgetTotal
				hasSeenPopup
				lastUpdated
				eventList {
					adminOverride
					approvalResult
					budgetAfterTotal
					budgetBeforeTotal
					completedById
					completedDate
					createdDate
					pendingApproval
					recognitionAmount
					recognitionId
					recognitionName
					sentById
					sentByManager
					sentReason
					snappyCampaignTierId
					snappyGrantId
				}
			}
		}
	}
`;

const UPDATE_BADGE_ASSIGNMENT = gql`
	mutation UpdateBadgeAssignment($input: UpdateBadgeAssignmentInput!) {
		updateBadgeAssignment(input: $input) {
			id
			accountClaimId
			approvalStatus
			awardedBy
			companyId
			userId
			sentWithoutSnappyGrant
			createdAt
			completedAt
			updatedAt
			badgeId
			managerAssigned
			declineReason
			quietDecline
			userGroupId
			badge {
				id
				name
				description
				points
				bonusPoints
				rewardBonus
				label
				badgeTypeId
				iconId
				color
				iconColor
				gradientColor
				snappyGrantId
				snappyGrant {
					id
					name
					costInCurrency
					externalSnappyId
					snappyCampaignTierId
				}
				isAssignmentRestricted
				shape
				category
			}
			content {
				message
				gifURL
				tags
			}
			rewardBonus
			justificationFormAnswers
			recipients {
				accountClaimId
				accountClaim {
					id
					firstName
					lastName
					emailAddress
					branchLocation
					subCompany
					department
					employeeId
					uniqueEmployeeId
					userId
				}
				userId
				user {
					id
					firstName
					lastName
					emailAddress
					departmentId
					employeeId
					subCompanyId
					userGroupId
				}
			}
			user {
				id
				firstName
				lastName
				emailAddress
				recognitionEvents {
					currentBudget
				}
			}
			awardedByUser {
				id
				firstName
				lastName
				emailAddress
				employeeId
			}
			accountClaim {
				id
				firstName
				lastName
				emailAddress
				subCompany
				department
				employeeId
				userId
			}
		}
	}
`;

const UPDATE_USER = gql`
	mutation UpdateUser($input: UpdateUserInput!) {
		updateUser(input: $input) {
			id
			recognitionEvents {
				currentBudget
				amountPending
				additionalBudgetTotal
				hasSeenPopup
				lastUpdated
				eventList {
					adminOverride
					approvalResult
					budgetAfterTotal
					budgetBeforeTotal
					completedById
					completedDate
					createdDate
					pendingApproval
					recognitionAmount
					recognitionId
					recognitionName
					sentById
					sentByManager
					sentReason
					snappyCampaignTierId
					snappyGrantId
				}
			}
		}
	}
`;

const CREATE_SNAPPY_GIFT = gql`
	mutation CreateSnappyGift($input: CreateSnappyGiftInput!) {
		createSnappyGift(input: $input) {
			id
			giftId
			campaignTierId
			snappyGrantId
			status
			giftDetails {
				link
				status
			}
			createdAt
		}
	}
`;

// Loading skeleton component
const SummaryCardSkeleton = () => (
	<div
		className="contact-card"
		style={{
			backgroundColor: 'white',
			padding: '16px',
			border: '1px solid #E5E5E5',
			borderRadius: '10px',
			minHeight: '156px',
			width: '50%',
		}}
	>
		<div className="contact-card-inner">
			<div className="contact-card-title">
				<Skeleton.Input style={{ width: 150, height: 16 }} active />
			</div>
			<div
				className="contact-color-card"
				style={{ display: 'flex', gap: '8px', marginTop: '16px' }}
			>
				<Skeleton.Input style={{ flex: 1, height: 80 }} active />
				<Skeleton.Input style={{ flex: 1, height: 80 }} active />
				<Skeleton.Input style={{ flex: 1, height: 80 }} active />
			</div>
		</div>
	</div>
);

const ApprovalRewardsContainer = (props) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	let scopedPolicy = currentUser?.scopedPolicy || {};
	if (typeof scopedPolicy === 'string') {
		try {
			scopedPolicy = JSON.parse(scopedPolicy);
		} catch (e) {
			scopedPolicy = {};
			console.error(e);
		}
	}
	const client = useApolloClient();
	const companyId = currentUser?.company?.id;
	const recognitionSettings = currentUser?.company?.recognitionSettings || {};
	const { baseSnappyBudget = 0 } = recognitionSettings;
	let subCompanyIds = [];
	if (currentUser?.isScopedUser) {
		subCompanyIds = (scopedPolicy?.entities?.sub_companies || []).filter(
			(id) => !id.startsWith('!')
		);
	}

	// State management
	const [formData, setFormData] = useState(null);
	const [isFormDataLoading, setIsFormDataLoading] = useState(false);
	const [pendingRecordId, setPendingRecordId] = useState(null);
	const [pendingRecord, setPendingRecord] = useState(null);
	const [isApprove, setIsApprove] = useState(false);
	const [isDecline, setIsDecline] = useState(false);
	const [isBudgetModal, setIsBudgetModal] = useState(false);
	const [isProcessingBudgetOverride, setIsProcessingBudgetOverride] =
		useState(false);
	const [userRecognitionCache, setUserRecognitionCache] = useState(new Map());
	const [includeAutomated, setIncludeAutomated] = useState(false);

	const { loading, error, data, fetchMore, refetch } = useQuery(
		QUERY_BADGE_ASSIGNMENTS_ADMIN,
		{
			variables: {
				companyId: companyId,
				filterAutomated: !includeAutomated,
				subCompanyIds: subCompanyIds,
				nextToken: null,
				limit: 100,
			},
			skip: !companyId,
			fetchPolicy: 'cache-and-network',
			notifyOnNetworkStatusChange: true,
		}
	);

	useEffect(() => {
		if (!isFormDataLoading && !formData && companyId) {
			const loadFormData = async () => {
				try {
					setIsFormDataLoading(true);
					const awaitedFormData =
						(await fetchModernFormQuestions(
							currentUser,
							'custom',
							'manager_justification_form',
							client
						)) || {};
					setFormData(awaitedFormData);
				} catch (e) {
					console.error('Error loading form data:', e);
					setFormData({});
				} finally {
					setIsFormDataLoading(false);
				}
			};
			loadFormData();
		}
	}, [companyId, currentUser, client, formData, isFormDataLoading]);

	useEffect(() => {
		if (data?.queryBadgeAssignmentsAdmin?.nextToken && !error) {
			setTimeout(() => {
				fetchMore({
					variables: {
						companyId: companyId,
						filterAutomated: !includeAutomated,
						nextToken: data.queryBadgeAssignmentsAdmin.nextToken,
						limit: 200,
					},
					updateQuery: (prev, { fetchMoreResult }) => {
						if (!fetchMoreResult) return prev;
						return {
							queryBadgeAssignmentsAdmin: {
								...fetchMoreResult.queryBadgeAssignmentsAdmin,
								items: [
									...prev.queryBadgeAssignmentsAdmin.items,
									...fetchMoreResult.queryBadgeAssignmentsAdmin.items,
								],
							},
						};
					},
				});
			}, 100);
		}
	}, [data, includeAutomated, fetchMore, companyId, error]);

	const items = useMemo(
		() => data?.queryBadgeAssignmentsAdmin?.items || [],
		[data?.queryBadgeAssignmentsAdmin?.items]
	);

	const { pendingAssignments, approvedAssignments, declinedAssignments } =
		useMemo(() => {
			if (!items.length)
				return {
					pendingAssignments: [],
					approvedAssignments: [],
					declinedAssignments: [],
				};

			const pending = [];
			const approved = [];
			const declined = [];

			for (const item of items) {
				switch (item.approvalStatus) {
					case 'Pending':
						pending.push(item);
						break;
					case 'Approved':
						approved.push(item);
						break;
					case 'Declined':
						declined.push(item);
						break;
				}
			}

			pending.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

			return {
				pendingAssignments: pending,
				approvedAssignments: approved,
				declinedAssignments: declined,
			};
		}, [items]);

	const approvedDeclinedAssignments = useMemo(() => {
		if (!approvedAssignments.length && !declinedAssignments.length) return [];
		return [...approvedAssignments, ...declinedAssignments].sort(
			(a, b) => new Date(a.createdAt) - new Date(b.createdAt)
		);
	}, [approvedAssignments, declinedAssignments]);

	const { totalBonus, approvedBonus, pendingBonus } = useMemo(() => {
		let total = 0;
		let approved = 0;
		let pending = 0;

		for (const item of items) {
			const cost = item.badge?.snappyGrant?.costInCurrency || 0;
			total += cost;

			if (item.approvalStatus === 'Approved') {
				approved += cost;
			} else if (item.approvalStatus === 'Pending') {
				pending += cost;
			}
		}

		return {
			totalBonus: total,
			approvedBonus: approved,
			pendingBonus: pending,
		};
	}, [items]);

	const countData = useMemo(
		() => ({
			approvalStatusCounts: [
				{
					name: 'Approved',
					totals: approvedAssignments.length,
					color: 'oklch(0.7903 0.136 192.97)',
				},
				{
					name: 'Pending',
					totals: pendingAssignments.length,
					color: 'oklch(0.6757 0.187 349.98)',
				},
				{
					name: 'Declined',
					totals: declinedAssignments.length,
					color: 'oklch(0.58 0.26 295)',
				},
			],
			rewardPointsValues: [
				{
					name: 'Total Rewards',
					totals: totalBonus,
					color: 'oklch(0.5307 0.1791 144.1)',
				},
				{
					name: 'Approved',
					totals: approvedBonus,
					color: 'oklch(0.7099 0.1414 141.23)',
				},
				{
					name: 'Pending',
					totals: pendingBonus,
					color: 'oklch(0.8462 0.1426 141.19)',
				},
			],
		}),
		[
			approvedAssignments.length,
			pendingAssignments.length,
			declinedAssignments.length,
			totalBonus,
			approvedBonus,
			pendingBonus,
		]
	);

	const getUserRecognitionEvents = useCallback(
		async (userId) => {
			if (userRecognitionCache.has(userId)) {
				return userRecognitionCache.get(userId);
			}

			try {
				const { data } = await client.query({
					query: QUERY_USER_RECOGNITION_EVENTS,
					variables: { userId },
					fetchPolicy: 'cache-first',
				});

				const recognitionEvents = data?.getUser?.recognitionEvents;
				setUserRecognitionCache(
					(prev) => new Map(prev.set(userId, recognitionEvents))
				);
				return recognitionEvents;
			} catch (error) {
				console.error('Error fetching user recognition events:', error);
				return null;
			}
		},
		[client, userRecognitionCache]
	);

	const getJustificationContent = useCallback(
		(justificationData = null) => {
			if (justificationData === null || !formData?.elements) return [];

			try {
				const parsedJustificationData = JSON.parse(justificationData);
				const content = [];
				const validElements = ['input', 'textarea', 'select'];

				formData.elements.forEach((ele) => {
					if (validElements.includes(ele?.type)) {
						const answer = parsedJustificationData?.[ele.id] || '';
						content.push({ question: ele?.label, answer });
					}
				});
				return content;
			} catch (error) {
				console.error('Error parsing justification data:', error);
				return [];
			}
		},
		[formData]
	);

	const SummaryStatBox = useCallback(
		({ color = '', statName, label, total, title, index }) => {
			const motionProps = {
				initial: { opacity: 0, scale: 0.9 },
				animate: { opacity: 1, scale: 1 },
				transition: { duration: 0.5, delay: (index + 1) / 10, ease: 'easeOut' },
				whileHover: { scale: 1.02, transition: { duration: 0.2 } },
			};

			const cardStyle = {
				flex: 1,
				boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
				width: '33.33333%',
				background: color,
			};

			return (
				<motion.div className="color-card" style={cardStyle} {...motionProps}>
					<h4>
						{' '}
						{title === ' Bonuses' && `$`} {addCommas(total)}
					</h4>
					<p>
						{label ? label : statName === 'Total Rewards' ? 'Total' : statName}
					</p>
				</motion.div>
			);
		},
		[]
	);

	const motionContainerProps = useMemo(
		() => ({
			initial: { opacity: 0, y: 20 },
			animate: { opacity: 1, y: 0 },
			transition: { duration: 0.6, ease: 'easeOut' },
		}),
		[]
	);

	const approvalStatusBoxes = useMemo(() => {
		if (!countData?.approvalStatusCounts?.length) return null;
		return countData.approvalStatusCounts.map((reward, index) => (
			<SummaryStatBox
				key={`approval-${reward.name}-${index}`}
				color={reward.color}
				statName={reward.name}
				total={reward.totals}
				index={index}
			/>
		));
	}, [countData?.approvalStatusCounts, SummaryStatBox]);

	const rewardPointsBoxes = useMemo(() => {
		if (!countData?.rewardPointsValues?.length) return null;
		return countData.rewardPointsValues.map((reward, index) => (
			<SummaryStatBox
				key={`reward-${reward.name}-${index}`}
				color={reward.color}
				statName={reward.name}
				total={`$${reward.totals}`}
				index={index}
			/>
		));
	}, [countData?.rewardPointsValues, SummaryStatBox]);

	const approvalStatusSection = useMemo(
		() => (
			<motion.div
				className="contact-color-card"
				style={{}}
				{...motionContainerProps}
			>
				{approvalStatusBoxes}
			</motion.div>
		),
		[approvalStatusBoxes, motionContainerProps]
	);

	const rewardPointsSection = useMemo(
		() => (
			<motion.div
				className="contact-color-card"
				style={{}}
				{...motionContainerProps}
			>
				{rewardPointsBoxes}
			</motion.div>
		),
		[rewardPointsBoxes, motionContainerProps]
	);

	if (error) return <div>Error loading data: {error.message}</div>;

	const isInitialLoading = loading && !items.length;

	const handleApproveAndDeclined = async (
		recordId,
		status,
		record = null,
		declineReason = null,
		quietDecline = false
	) => {
		if (!recordId) {
			message.error('Record ID not found');
			return;
		}

		if (status === 'Declined') {
			try {
				const { data } = await props.client.mutate({
					mutation: UPDATE_BADGE_ASSIGNMENT,
					variables: {
						input: {
							id: recordId,
							companyId: companyId,
							approvalStatus: 'Declined',
							completedAt: new Date().toISOString(),
							declineReason: declineReason,
							quietDecline: quietDecline,
						},
					},
				});

				if (data?.updateBadgeAssignment) {
					setIsDecline(false);
					setIsApprove(false);
					message.success('Recognition declined successfully');
				} else {
					message.error('Failed to decline recognition');
				}
			} catch (error) {
				console.error('Error declining recognition:', error);
				message.error('Failed to decline recognition');
			}
			return;
		}

		if (status === 'Approved') {
			const currentRecord = record || pendingRecord;
			if (!currentRecord) {
				message.error('Record data not found');
				return;
			}

			const recognitionEvents =
				currentRecord?.userId !== 'account_claim'
					? await getUserRecognitionEvents(currentRecord.userId)
					: null;
			const currentBudget =
				recognitionEvents && recognitionEvents?.currentBudget !== undefined
					? recognitionEvents.currentBudget
					: baseSnappyBudget || 0;

			const totalCost = currentRecord?.badge?.snappyGrant?.costInCurrency || 0;

			if (currentBudget < totalCost && totalCost > 0) {
				setIsBudgetModal(true);
				return;
			}

			// Create snappy gift if the badge assignment has a reward
			if (currentRecord.badge?.snappyGrant?.externalSnappyId) {
				await createSnappyGiftForRecipient(currentRecord);
			}

			await approveBadgeAssignment(recordId, currentRecord, recognitionEvents);
		}
	};

	const approveBadgeAssignment = async (
		recordId,
		record,
		recognitionEvents = null,
		sentWithoutSnappyGrant = false,
		overrideBudget = false
	) => {
		try {
			const updateInput = {
				id: recordId,
				companyId: companyId,
				approvalStatus: 'Approved',
				completedAt: new Date().toISOString(),
				sentWithoutSnappyGrant: sentWithoutSnappyGrant,
			};

			const { data } = await props.client.mutate({
				mutation: UPDATE_BADGE_ASSIGNMENT,
				variables: { input: updateInput },
			});

			if (data?.updateBadgeAssignment) {
				if (
					record?.badge?.snappyGrant?.costInCurrency > 0 &&
					record?.userId !== 'account_claim'
				) {
					await updateUserRecognitionEvents(
						record,
						recognitionEvents,
						sentWithoutSnappyGrant,
						overrideBudget
					);
				}

				setIsDecline(false);
				setIsApprove(false);
				setIsBudgetModal(false);
				setIsProcessingBudgetOverride(false);
				message.success('Recognition approved successfully');
			} else {
				message.error('Failed to approve recognition');
			}
		} catch (error) {
			console.error('Error approving recognition:', error);
			message.error('Failed to approve recognition');
		}
	};

	const createSnappyGiftForRecipient = async (badgeAssignment) => {
		if (
			badgeAssignment?.recipients?.length > 1 ||
			badgeAssignment?.userId === 'account_claim'
		) {
			return;
		} else {
			try {
				const snappyGiftInput = {
					id: uuid(),
					companyId: companyId,
					userId: badgeAssignment.userId,
					badgeAssignmentId: badgeAssignment.id,
					snappyGrantId: badgeAssignment.badge.snappyGrantId,
					externalSnappyGiftId:
						badgeAssignment.badge.snappyGrant.externalSnappyId,
					claimedOn: new Date().toISOString(),
				};

				await snappy({
					type: 'directCreateGift',
					sendingMethod: 'link',
					snappyCampaignId: badgeAssignment.badge.snappyGrant.externalSnappyId,
					snappyGiftInput: snappyGiftInput,
				});
			} catch (error) {
				console.error('Error creating snappy gift:', error);
			}
		}
	};

	const updateUserRecognitionEvents = async (
		record,
		recognitionEvents = null,
		sentWithoutSnappyGrant = false,
		overrideBudget = false
	) => {
		try {
			const userId = record?.userId;
			const costInCurrency = record?.badge?.snappyGrant?.costInCurrency || 0;
			const baseSnappyBudget =
				currentUser?.company?.recognitionSettings?.baseSnappyBudget || 0;

			const currentRecognitionEvents =
				recognitionEvents || (await getUserRecognitionEvents(userId));
			const currentBudget =
				currentRecognitionEvents?.currentBudget !== undefined
					? currentRecognitionEvents.currentBudget
					: baseSnappyBudget;

			const newBudget = overrideBudget ? 0 : currentBudget - costInCurrency;

			const newTransaction = {
				adminOverride: overrideBudget,
				approvalResult: true,
				budgetAfterTotal: sentWithoutSnappyGrant ? currentBudget : newBudget,
				budgetBeforeTotal: currentBudget,
				completedById: currentUser.id,
				completedDate: new Date().toISOString(),
				createdDate: new Date().toISOString(),
				pendingApproval: false,
				recognitionAmount: sentWithoutSnappyGrant ? 0 : costInCurrency || 0,
				recognitionId: record?.badge?.id,
				recognitionName: record?.badge?.name,
				sentById: record.awardedBy,
				sentByManager: record?.managerAssigned || false,
				sentReason: 'Badge Assignment Approval',
				snappyCampaignTierId: sentWithoutSnappyGrant
					? null
					: record?.badge?.snappyGrant?.id || null,
				snappyGrantId: sentWithoutSnappyGrant
					? null
					: record?.badge?.snappyGrantId || null,
			};

			const recognitionEventsBase = currentRecognitionEvents || {
				currentBudget: baseSnappyBudget,
				amountPending: 0,
				additionalBudgetTotal: 0,
				hasSeenPopup: false,
				eventList: [],
			};

			let cleanedEventList = recognitionEventsBase.eventList.map((event) => {
				const { ...cleanEvent } = event;
				return cleanEvent;
			});

			let newEventList = [...cleanedEventList];
			newEventList.push(newTransaction);

			let updatedRecognitionEvents = {
				...recognitionEventsBase,
				currentBudget: sentWithoutSnappyGrant ? currentBudget : newBudget,
				amountPending: sentWithoutSnappyGrant
					? recognitionEventsBase?.amountPending
					: recognitionEventsBase?.amountPending > 0
						? recognitionEventsBase?.amountPending - costInCurrency
						: 0,
				additionalBudgetTotal: sentWithoutSnappyGrant
					? recognitionEventsBase?.additionalBudgetTotal || 0
					: (recognitionEventsBase.additionalBudgetTotal || 0) +
						(overrideBudget ? costInCurrency - currentBudget : 0),
				lastUpdated: new Date().toISOString(),
				eventList: newEventList,
			};

			await props.client.mutate({
				mutation: UPDATE_USER,
				variables: {
					input: {
						id: userId,
						recognitionEvents: updatedRecognitionEvents,
					},
				},
			});

			setUserRecognitionCache(
				(prev) => new Map(prev.set(userId, updatedRecognitionEvents))
			);
		} catch (error) {
			console.log('Error updating user recognition events:', error);
		}
	};

	const handleApprovalClick = (record) => {
		setPendingRecordId(record?.id);
		setPendingRecord(record);
		setIsApprove(true);
	};

	const handleDeclineClick = (record) => {
		setPendingRecordId(record?.id);
		setPendingRecord(record);
		setIsDecline(true);
	};

	return (
		<div>
			<div
				className="page-title"
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<h2 className="page-heading">All Recognitions</h2>
				<Checkbox
					checked={includeAutomated}
					onChange={(e) => setIncludeAutomated(e.target.checked)}
				>
					Include Automated
				</Checkbox>
			</div>

			<motion.div
				style={{
					display: 'flex',
					flexDirection: 'row',
					width: '100%',
					gap: 16,
					marginBottom: 20,
				}}
			>
				{isInitialLoading ? (
					<>
						<SummaryCardSkeleton />
						<SummaryCardSkeleton />
					</>
				) : (
					<>
						<div
							className="contact-card"
							style={{
								backgroundColor: 'white',
								padding: '16px',
								border: '1px solid #E5E5E5',
								borderRadius: '10px',
								minHeight: '156px',
								width: '50%',
							}}
						>
							<div className="contact-card-inner">
								<div className="contact-card-title">
									<p
										style={{
											fontSize: '14px',
											fontWeight: '800',
											color: 'var(--dove-gray)',
										}}
									>
										Recognition Statuses
									</p>
								</div>
								{approvalStatusSection}
							</div>
						</div>
						<div
							className="contact-card"
							style={{
								backgroundColor: 'white',
								padding: '16px',
								border: '1px solid #E5E5E5',
								borderRadius: '10px',
								minHeight: '156px',
								width: '50%',
							}}
						>
							<div className="contact-card-inner">
								<div className="contact-card-title">
									<p
										style={{
											fontSize: '14px',
											fontWeight: '800',
											color: 'var(--dove-gray)',
										}}
									>
										Recognition Totals
									</p>
								</div>
								{rewardPointsSection}
							</div>
						</div>
					</>
				)}
			</motion.div>

			{isInitialLoading ? (
				<div style={{ padding: '20px' }}>
					<Skeleton active paragraph={{ rows: 8 }} />
				</div>
			) : (
				<>
					<ApprovedContainer
						tableData={pendingAssignments}
						isPending={true}
						isApprove={isApprove}
						isDecline={isDecline}
						setIsDecline={setIsDecline}
						setIsApprove={setIsApprove}
						handleApprovalClick={handleApprovalClick}
						handleDeclineClick={handleDeclineClick}
						handleApproveAndDeclined={handleApproveAndDeclined}
						getJustificationContent={getJustificationContent}
						pendingRecordId={pendingRecordId}
						pendingRecord={pendingRecord}
					/>
					{approvedDeclinedAssignments.length > 0 && (
						<ApprovedContainer
							tableData={approvedDeclinedAssignments}
							isPendingReward={false}
							pendingRecordId={pendingRecordId}
							getJustificationContent={getJustificationContent}
						/>
					)}
				</>
			)}

			<Modal
				destroyOnClose
				width={500}
				open={isBudgetModal}
				title="Budget Override Required"
				footer={null}
				className="confirmation-modal"
				onCancel={() => {
					setIsBudgetModal(false);
					setIsProcessingBudgetOverride(false);
				}}
			>
				<div>
					<p
						className="text-center modal-detail"
						style={{ marginBottom: '20px' }}
					>
						Reward would be more than the employee's total budget, do you want
						to send the Recognition without a reward attached?
					</p>
					<div
						className="text-center"
						style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}
					>
						<Button
							className="approve-btn"
							type="primary"
							disabled={isProcessingBudgetOverride}
							onClick={async () => {
								const recognitionEvents = await getUserRecognitionEvents(
									pendingRecord?.userId
								);
								await approveBadgeAssignment(
									pendingRecordId,
									pendingRecord,
									recognitionEvents,
									true,
									false
								);
							}}
							style={{
								marginRight: '10px',
								filter: isProcessingBudgetOverride ? 'grayscale(1)' : null,
							}}
						>
							Send Recognition Only
						</Button>
						<Button
							className="approve-btn"
							loading={isProcessingBudgetOverride}
							disabled={isProcessingBudgetOverride}
							onClick={async () => {
								setIsProcessingBudgetOverride(true);
								try {
									if (pendingRecord?.badge?.snappyGrant?.externalSnappyId) {
										await createSnappyGiftForRecipient(pendingRecord);
									}
									const recognitionEvents = await getUserRecognitionEvents(
										pendingRecord?.userId
									);
									await approveBadgeAssignment(
										pendingRecordId,
										pendingRecord,
										recognitionEvents,
										false,
										true
									);
								} finally {
									setIsProcessingBudgetOverride(false);
								}
							}}
							style={{
								marginRight: '10px',
								filter: isProcessingBudgetOverride ? 'grayscale(1)' : null,
							}}
						>
							{isProcessingBudgetOverride ? 'Processing...' : 'Override Budget'}
						</Button>
						<Button
							className="decline-btn"
							disabled={isProcessingBudgetOverride}
							onClick={() => {
								setIsDecline(false);
								setIsApprove(false);
								setIsBudgetModal(false);
								setIsProcessingBudgetOverride(false);
							}}
							style={{
								filter: isProcessingBudgetOverride ? 'grayscale(1)' : null,
							}}
						>
							Cancel
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default withRouter(withApollo(ApprovalRewardsContainer));
